/**
 * Metadatový katalog API Tools operací. Každá operace = skupina + pole; runner
 * (ApiToolsPage) renderuje pole genericky, sesbírá hodnoty a POSTne na execute
 * endpoint. Fáze E5a pokrývá skupinu `token` (6 operací); tx/dr/kbcaas přijdou v
 * dalších částech.
 */

export type FieldKind =
  | "text"
  | "number"
  | "textarea"
  | "select"
  | "checkbox"
  | "tags"
  | "tokenSelect"
  | "paatSelect"
  | "authServerType"
  | "connectorAttrs"
  | "password"
  | "passwordType"
  | "jsonArray"
  | "pushId"
  | "otpVerify";

export interface FieldSpec {
  name: string;
  /** i18n klíč labelu. */
  label: string;
  kind: FieldKind;
  required?: boolean;
  /** i18n klíč nápovědy. */
  hint?: string;
  /** Pro `select`: pevné volby (value + i18n label klíč nebo přímý text). */
  options?: { value: string; label: string }[];
  /**
   * Pro `select`: vykreslit jako taby nad formulářem místo rozbalovacího seznamu.
   * Určeno pro pole, která přepínají *režim* operace (a tím i sadu ostatních polí
   * přes `showWhen`) — v jednom společném formuláři se taková volba ztratí a není
   * poznat, které pole k jakému režimu patří. Taby se renderují nahoře, nezávisle
   * na pozici pole v seznamu.
   */
  asTabs?: boolean;
  /**
   * Skupina „aspoň jedno z". Pole se stejnou hodnotou tvoří množinu, ze které musí
   * být vyplněné alespoň jedno — doplněk k `required`, které platí na jedno konkrétní
   * pole. Vyhodnocuje se jen nad viditelnými poli, takže se to skládá se `showWhen`
   * i `connectorOnly`.
   */
  requiredOneOf?: string;
  /** Pro `tokenSelect`: jen ACTIVE tokeny. */
  activeOnly?: boolean;
  /** Pole se renderuje jen pro eTTS loopback tab. */
  ettsOnly?: boolean;
  /** Pole se renderuje jen pro daný connector id (např. `echannels` pro ippids). */
  connectorOnly?: string;
  /** Hodnota se posílá do `connectorAttributes[name]` místo top-level body. */
  intoAttrs?: boolean;
  /** Podmíněné zobrazení: jen když hodnota jiného pole == value. */
  showWhen?: { field: string; value: string };
  default?: string;
}

export type OperationGroup = "token" | "tx" | "dr" | "kbcaas" | "privat";

export interface OperationSpec {
  id: string;
  group: OperationGroup;
  /** i18n klíč. */
  label: string;
  fields: FieldSpec[];
}

export interface OperationGroupSpec {
  group: OperationGroup;
  /** i18n klíč názvu skupiny. */
  label: string;
  operations: OperationSpec[];
}

const TOKEN_OPERATIONS: OperationSpec[] = [
  {
    id: "activateToken",
    group: "token",
    label: "apitools.op.activateToken",
    fields: [
      { name: "authServerType", label: "apitools.f.authServerType", kind: "authServerType", ettsOnly: true, required: true },
      { name: "username", label: "apitools.f.username", kind: "text", required: true },
      // OTP se posílá jen u OTP varianty aktivace. U TOKEN varianty ho konektor
      // ignoruje (aktivuje se OAuth access tokenem), takže by ho pole `required`
      // jen blokovalo. `activationMethod` má default "OTP", takže u konektorů bez
      // téhle volby je pole viditelné vždy.
      { name: "otp", label: "apitools.f.otp", kind: "text", required: true, showWhen: { field: "activationMethod", value: "OTP" } },
      { name: "password", label: "apitools.f.password", kind: "text", required: true },
      { name: "activationGuid", label: "apitools.f.activationGuid", kind: "text" },
      { name: "paatProfileId", label: "apitools.f.paatProfile", kind: "paatSelect" },
      { name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" },
      { name: "expiresInSeconds", label: "apitools.f.expiresInSeconds", kind: "number", hint: "apitools.f.expiresInSeconds.hint" },
      { name: "tags", label: "apitools.f.tags", kind: "tags", hint: "apitools.f.tags.hint" },
      { name: "connectorAttributes", label: "apitools.f.connectorAttributes", kind: "connectorAttrs", ettsOnly: true },
      // --- connector-specific aktivační atributy (jdou do connectorAttributes) ---
      // eChannels
      { name: "pushIdentifier", label: "apitools.f.pushIdentifier", kind: "pushId", connectorOnly: "echannels", intoAttrs: true, required: true },
      { name: "applicationId", label: "apitools.f.applicationId", kind: "text", connectorOnly: "echannels", intoAttrs: true, default: "SmartToken" },
      { name: "isMigration", label: "apitools.f.isMigration", kind: "checkbox", connectorOnly: "echannels", intoAttrs: true },
      { name: "deviceType", label: "apitools.f.deviceType", kind: "number", connectorOnly: "echannels", intoAttrs: true },
      // eCobra
      { name: "applicationId", label: "apitools.f.applicationId", kind: "text", connectorOnly: "ecobra", intoAttrs: true, required: true, default: "SmartToken" },
      { name: "pushIdentifier", label: "apitools.f.pushIdentifier", kind: "pushId", connectorOnly: "ecobra", intoAttrs: true },
      // Privat
      { name: "pushIdentifier", label: "apitools.f.pushIdentifier", kind: "pushId", connectorOnly: "privat", intoAttrs: true },
      { name: "applicationId", label: "apitools.f.applicationId", kind: "text", connectorOnly: "privat", intoAttrs: true },
      // KB CAAS
      {
        name: "activationMethod",
        label: "apitools.f.activationMethod",
        kind: "select",
        asTabs: true,
        connectorOnly: "kb-caas",
        intoAttrs: true,
        default: "OTP",
        options: [
          { value: "OTP", label: "OTP" },
          { value: "TOKEN", label: "TOKEN" },
        ],
      },
      {
        name: "appType",
        label: "apitools.f.appType",
        kind: "select",
        connectorOnly: "kb-caas",
        intoAttrs: true,
        default: "STANDALONE",
        required: true,
        options: [
          { value: "STANDALONE", label: "STANDALONE" },
          { value: "NDB_SDK", label: "NDB_SDK" },
        ],
      },
      { name: "applicationName", label: "apitools.f.applicationName", kind: "text", connectorOnly: "kb-caas", intoAttrs: true, required: true },
      // Ověřovací OTP se čte z testovacího přístupu, který zná jen KBID — při aktivaci pod
      // jménem zvoleným klientem se musí KBID doplnit sem, jinak se OTP nedohledá.
      { name: "kbId", label: "apitools.f.kbId", kind: "text", connectorOnly: "kb-caas", intoAttrs: true, hint: "apitools.f.kbId.hint", showWhen: { field: "activationMethod", value: "OTP" } },
      { name: "sourceTokenId", label: "apitools.f.sourceTokenId", kind: "tokenSelect", connectorOnly: "kb-caas", intoAttrs: true, requiredOneOf: "kbCaasTokenSource", showWhen: { field: "activationMethod", value: "TOKEN" } },
      { name: "accessToken", label: "apitools.f.accessToken", kind: "text", connectorOnly: "kb-caas", intoAttrs: true, requiredOneOf: "kbCaasTokenSource", hint: "apitools.f.accessToken.hint", showWhen: { field: "activationMethod", value: "TOKEN" } },
      { name: "codeVerifier", label: "apitools.f.codeVerifier", kind: "text", connectorOnly: "kb-caas", intoAttrs: true, hint: "apitools.f.codeVerifier.hint", showWhen: { field: "activationMethod", value: "TOKEN" } },
    ],
  },
  {
    id: "listTokens",
    group: "token",
    label: "apitools.op.listTokens",
    fields: [
      {
        name: "state",
        label: "apitools.f.state",
        kind: "select",
        options: [
          { value: "", label: "apitools.any" },
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "WAITING_FOR_ACTIVATION", label: "WAITING_FOR_ACTIVATION" },
          { value: "TEMPORARY_BLOCKED", label: "TEMPORARY_BLOCKED" },
          { value: "BLOCKED", label: "BLOCKED" },
          { value: "DELETED", label: "DELETED" },
        ],
      },
      { name: "username", label: "apitools.f.username", kind: "text" },
      { name: "tag", label: "apitools.f.tag", kind: "text" },
    ],
  },
  {
    id: "getToken",
    group: "token",
    label: "apitools.op.getToken",
    fields: [{ name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", required: true }],
  },
  {
    id: "instanceInfo",
    group: "token",
    label: "apitools.op.instanceInfo",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", required: true },
      // Bez zaškrtnutí jde o zákaznické čtení, se zaškrtnutím o admin zápis, který
      // zjištěný stav uloží do tokenu. Který se provolal, je vidět v echu requestu.
      { name: "syncState", label: "apitools.f.syncState", kind: "checkbox", hint: "apitools.f.syncState.hint" },
    ],
  },
  {
    id: "deactivateToken",
    group: "token",
    label: "apitools.op.deactivateToken",
    fields: [{ name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true }],
  },
  {
    id: "generateOtp",
    group: "token",
    label: "apitools.op.generateOtp",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "password", label: "apitools.f.password", kind: "password", required: true },
    ],
  },
  {
    id: "verifyOtp",
    group: "token",
    label: "apitools.op.verifyOtp",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "otp", label: "apitools.f.otp", kind: "otpVerify", required: true },
      { name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" },
    ],
  },
];

export const TX_OPERATIONS: OperationSpec[] = [
  {
    id: "pendingTransactions",
    group: "tx",
    label: "apitools.op.pendingTransactions",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "ippids", label: "apitools.f.ippids", kind: "text", connectorOnly: "echannels", hint: "apitools.f.ippids.hint" },
      {
        name: "status",
        label: "apitools.f.status",
        kind: "select",
        default: "0",
        options: [
          { value: "0", label: "apitools.f.status.all" },
          { value: "1", label: "1" },
          { value: "2", label: "2" },
          { value: "3", label: "3" },
          { value: "4", label: "4" },
          { value: "5", label: "5" },
          { value: "6", label: "6" },
          { value: "7", label: "7" },
          { value: "8", label: "8" },
        ],
      },
      { name: "transactionId", label: "apitools.f.transactionId", kind: "text" },
      { name: "limit", label: "apitools.f.limit", kind: "number" },
      { name: "includeDetails", label: "apitools.f.includeDetails", kind: "checkbox", default: "true" },
    ],
  },
  {
    id: "transactionDetail",
    group: "tx",
    label: "apitools.op.transactionDetail",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "transactionId", label: "apitools.f.transactionId", kind: "text", required: true, hint: "apitools.f.transactionId.hint" },
    ],
  },
  {
    id: "approveTransaction",
    group: "tx",
    label: "apitools.op.approveTransaction",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "transactionId", label: "apitools.f.transactionId", kind: "text", required: true },
      { name: "secureMessage", label: "apitools.f.secureMessage", kind: "textarea", required: true, hint: "apitools.f.secureMessage.hint" },
      { name: "passwordType", label: "apitools.f.passwordType", kind: "passwordType", default: "valid" },
      { name: "recognitionType", label: "apitools.f.recognitionType", kind: "number", default: "0" },
      {
        name: "mode",
        label: "apitools.f.mode",
        kind: "select",
        default: "ONLINE",
        options: [
          { value: "ONLINE", label: "ONLINE" },
          { value: "OFFLINE", label: "OFFLINE" },
        ],
      },
    ],
  },
  {
    id: "rejectTransaction",
    group: "tx",
    label: "apitools.op.rejectTransaction",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "transactionId", label: "apitools.f.transactionId", kind: "text", required: true },
      { name: "reason", label: "apitools.f.reason", kind: "text" },
    ],
  },
];

export const DR_OPERATIONS: OperationSpec[] = [
  {
    id: "createDeviceReplacement",
    group: "dr",
    label: "apitools.op.createDeviceReplacement",
    fields: [{ name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true }],
  },
  {
    id: "processDeviceReplacement",
    group: "dr",
    label: "apitools.op.processDeviceReplacement",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "transactionId", label: "apitools.f.transactionId", kind: "text", required: true },
    ],
  },
  {
    id: "verifyActivation",
    group: "dr",
    label: "apitools.op.verifyActivation",
    fields: [
      { name: "authServerType", label: "apitools.f.authServerType", kind: "authServerType", ettsOnly: true },
      { name: "activationGuid", label: "apitools.f.activationGuid", kind: "text", required: true },
    ],
  },
  {
    id: "assembleSequences",
    group: "dr",
    label: "apitools.op.assembleSequences",
    fields: [
      { name: "sequences", label: "apitools.f.sequences", kind: "jsonArray", required: true, hint: "apitools.f.sequences.hint" },
    ],
  },
];

/** KB CAAS specifické tool operace (jen kb-caas tab). */
export const KB_TOOL_OPERATIONS: OperationSpec[] = [
  {
    id: "kbCaasGetInstanceInfo",
    group: "kbcaas",
    label: "apitools.op.kbCaasGetInstanceInfo",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" },
    ],
  },
  {
    id: "kbCaasEvaluateDevice",
    group: "kbcaas",
    label: "apitools.op.kbCaasEvaluateDevice",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" },
    ],
  },
  {
    id: "kbCaasInitContext",
    group: "kbcaas",
    label: "apitools.op.kbCaasInitContext",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" },
    ],
  },
  {
    id: "kbCaasDictionaryLatest",
    group: "kbcaas",
    label: "apitools.op.kbCaasDictionaryLatest",
    fields: [{ name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" }],
  },
  {
    id: "kbCaasGetDictionary",
    group: "kbcaas",
    label: "apitools.op.kbCaasGetDictionary",
    fields: [
      { name: "version", label: "apitools.f.version", kind: "number", required: true },
      { name: "testTraceId", label: "apitools.f.testTraceId", kind: "text" },
    ],
  },
];

/**
 * Operace specifické pro Privat banka (jen tab `privat`).
 *
 * <p>Přihlášení je tu proto, že bez access tokenu neprojde <b>žádné</b> další volání na
 * jejich API — token se jinak získává automaticky před každou operací, ale neviditelně,
 * takže se rozbité přihlášení projeví jako nesrozumitelná chyba u nesouvisející operace.
 * Je to totéž, co u eChannels děláme voláním IdP.
 */
export const PRIVAT_TOOL_OPERATIONS: OperationSpec[] = [
  {
    id: "login",
    group: "privat",
    label: "apitools.op.privatLogin",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
      { name: "force", label: "apitools.f.forceLogin", kind: "checkbox", hint: "apitools.f.forceLogin.hint" },
    ],
  },
  {
    id: "invalidateLogin",
    group: "privat",
    label: "apitools.op.privatInvalidateLogin",
    fields: [
      { name: "tokenId", label: "apitools.f.tokenId", kind: "tokenSelect", activeOnly: true, required: true },
    ],
  },
];

/** Skupiny operací společné všem konektorům (token, transakce, výměna zařízení). */
export const COMMON_GROUPS: OperationGroupSpec[] = [
  { group: "token", label: "apitools.group.token", operations: TOKEN_OPERATIONS },
  { group: "tx", label: "apitools.group.tx", operations: TX_OPERATIONS },
  { group: "dr", label: "apitools.group.dr", operations: DR_OPERATIONS },
];

/** Skupiny operací pro daný connector tab — konektor smí přidat vlastní skupinu. */
export function connectorGroups(connectorId: string): OperationGroupSpec[] {
  if (connectorId === "kb-caas") {
    return [...COMMON_GROUPS, { group: "kbcaas", label: "apitools.group.kbcaas", operations: KB_TOOL_OPERATIONS }];
  }
  if (connectorId === "privat") {
    // Přihlášení je předpokladem všeho ostatního, takže jde nahoru, ne na konec.
    return [{ group: "privat", label: "apitools.group.privat", operations: PRIVAT_TOOL_OPERATIONS }, ...COMMON_GROUPS];
  }
  return COMMON_GROUPS;
}

/**
 * Sestaví request body z (viditelných) polí operace. Většina operací je 1:1;
 * activateToken skládá connectorAttributes/tags/expiresInSeconds; number pole
 * se parsují. Pole s `intoAttrs` jdou do `connectorAttributes[name]` (per-konektor
 * aktivační atributy). Přijímá už FILTROVANÝ seznam polí (ettsOnly/connectorOnly/
 * showWhen řeší volající), aby se neposílaly atributy z cizího connector tabu.
 */
export function buildOperationBody(fields: FieldSpec[], values: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const attrs: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.name];

    // Per-konektor atributy → connectorAttributes (jen neprázdné, checkbox vždy).
    if (f.intoAttrs) {
      if (f.kind === "checkbox") {
        attrs[f.name] = !!v;
      } else if (f.kind === "number") {
        const raw = String(v ?? "").trim();
        if (raw !== "") attrs[f.name] = parseInt(raw, 10);
      } else {
        const s = v == null ? "" : String(v).trim();
        if (s !== "") attrs[f.name] = s;
      }
      continue;
    }

    switch (f.kind) {
      case "tags": {
        const arr = String(v ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        body.tags = arr.length > 0 ? arr : null;
        break;
      }
      case "number": {
        const raw = String(v ?? "").trim();
        body[f.name] = raw !== "" ? parseInt(raw, 10) : null;
        break;
      }
      case "checkbox":
        body[f.name] = !!v;
        break;
      case "connectorAttrs":
        body.connectorAttributes = v ?? {};
        break;
      case "passwordType":
        // Heslo se dořeší asynchronně v runneru (fetchTokenPassword) — tady nic.
        break;
      case "jsonArray": {
        const raw = String(v ?? "").trim();
        if (!raw) {
          body[f.name] = null;
          break;
        }
        try {
          const parsed = JSON.parse(raw);
          body[f.name] = Array.isArray(parsed) ? parsed : null;
        } catch {
          body[f.name] = null;
        }
        break;
      }
      default: {
        const s = v == null ? "" : String(v);
        body[f.name] = s === "" ? (f.required ? "" : null) : s;
      }
    }
  }
  if (Object.keys(attrs).length > 0) {
    body.connectorAttributes = { ...((body.connectorAttributes as Record<string, unknown>) ?? {}), ...attrs };
  }
  return body;
}
